import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useDuplicateFertilizerPack,
	useGetFertilizerPacks,
	useGetFertilizers,
	usePostPlanFertilization,
	type FertilizerPack,
} from '@practium/features/api';
import { useToast } from '@practium/ui';
import { APPLICATION_METHOD_LABELS, MONTH_NAMES_PT } from '../constants/maintenance.constants';
import { createPlanningSchema } from '../schemas/planning.schema';
import type { FertilizationPlanAdvice, MaintenanceSectorFertilization } from '../types';
import {
	buildBudgetExportSections,
	exportPlanningToExcel,
	exportPlanningToPdf,
	type PlanningExportData,
} from '../utils/planningExport';
import {
	fromApiPeriod,
	mapPlanningAdjustedDemand,
	mapPlanningBudgetTable,
	mapPlanningFertilizerDemand,
	mapSectorFertilizationsToPlanning,
	toApiPeriod,
	type PlanningSectorResult,
} from '../utils/planningTableMapper';
import { toProductPriceMap } from '../utils/productCosts';
import { useDemandAdjustment } from './useDemandAdjustment';
import type { FertilizerPackModalMode } from './useFertilizerPackForm';
import { usePlanningBudget } from './usePlanningBudget';
import { usePlanningCosts, type PlanningCostsSnapshot } from './usePlanningCosts';

export type PlanningDemandView = 'hectare' | 'sector';

export const PLANNING_DEMAND_VIEW_OPTIONS: { value: PlanningDemandView; label: string }[] = [
	{ value: 'hectare', label: 'Por hectare' },
	{ value: 'sector', label: 'Por setor' },
];
export type PlanningPackModalMode = FertilizerPackModalMode;

export interface PlanningFormState {
	method: '' | 'irrigation' | 'broad';
	startMonth: string;
	endMonth: string;
	packId: string;
}

type PlanningSnapshot = PlanningCostsSnapshot & {
	form: PlanningFormState;
	sectors: PlanningSectorResult[];
};

const EMPTY_FORM: PlanningFormState = {
	method: '',
	startMonth: '',
	endMonth: '',
	packId: '',
};

const generatedAtTime = (item: MaintenanceSectorFertilization) =>
	item.generatedAt ? new Date(item.generatedAt).getTime() : 0;

const latestEntry = (items: MaintenanceSectorFertilization[]) =>
	items.reduce(
		(latest, item) => (generatedAtTime(item) > generatedAtTime(latest) ? item : latest),
		items[0]
	);

const hydrateFromAdvice = (advice: FertilizationPlanAdvice): PlanningSnapshot | null => {
	const generated = advice.generatedRecommendations ?? [];
	if (!generated.length) return null;
	const latest = latestEntry(generated);
	const currentEntries = generated.filter((item) => item.method === latest.method);
	const periods = (currentEntries[0].periods ?? []).map(fromApiPeriod).filter(Boolean);
	const productPrices = advice.generatedCombo?.productPrices ?? [];
	return {
		sectors: mapSectorFertilizationsToPlanning(currentEntries),
		prices: toProductPriceMap(productPrices),
		pricesUpdatedAt: productPrices.length ? (latest.generatedAt ?? null) : null,
		form: {
			method: latest.method ?? '',
			startMonth: periods[0] ?? '',
			endMonth: periods[periods.length - 1] ?? periods[0] ?? '',
			packId: advice.generatedCombo?.packId ?? advice.packId ?? '',
		},
	};
};

interface UsePlanningFormOptions {
	advice: FertilizationPlanAdvice;
	onSaved?: (method: PlanningFormState['method']) => void;
}

export const usePlanningForm = ({ advice, onSaved }: UsePlanningFormOptions) => {
	const toast = useToast();
	const adviceId = advice._id ?? '';
	const hydrated = useMemo(() => hydrateFromAdvice(advice), [advice]);
	const [form, setForm] = useState<PlanningFormState>(() => hydrated?.form ?? EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [sectors, setSectors] = useState<PlanningSectorResult[]>(() => hydrated?.sectors ?? []);
	const [persisted, setPersisted] = useState<PlanningSnapshot | null>(() => hydrated);
	const [isView, setIsView] = useState(() => (hydrated?.sectors.length ?? 0) > 0);
	const [demandView, setDemandView] = useState<PlanningDemandView>('hectare');
	const [packModal, setPackModal] = useState<{
		open: boolean;
		mode: PlanningPackModalMode;
		pack: FertilizerPack | null;
	}>({ open: false, mode: 'add', pack: null });

	const { data: packs = [] } = useGetFertilizerPacks();
	const { data: fertilizers = [] } = useGetFertilizers({ limit: 300 });
	const { mutateAsync: calculatePlan, isPending: isCalculating } = usePostPlanFertilization();
	const { mutateAsync: duplicatePack, isPending: isDuplicatingPack } = useDuplicateFertilizerPack();

	const selectedPack = packs.find((pack) => String(pack._id) === String(form.packId)) ?? null;
	const costs = usePlanningCosts({ initial: hydrated, selectedPack, fertilizers, isView });
	const budget = usePlanningBudget(sectors, fertilizers);

	useEffect(() => {
		const next = hydrateFromAdvice(advice);
		setForm(next?.form ?? EMPTY_FORM);
		setErrors({});
		setSectors(next?.sectors ?? []);
		costs.restoreCosts(next);
		setPersisted(next);
		setIsView((next?.sectors.length ?? 0) > 0);
		setDemandView('hectare');
	}, [adviceId]);

	const showIrrigationFields = Boolean(
		form.method && form.startMonth && form.endMonth && form.method === 'irrigation'
	);
	const hasCalculated = sectors.length > 0;

	const planMonths = useMemo(
		() => (advice.periods ?? []).map(fromApiPeriod).filter(Boolean),
		[advice.periods]
	);
	const planningSchema = useMemo(() => createPlanningSchema(planMonths), [planMonths]);
	const packOptions = packs.map((pack) => ({ value: String(pack._id), label: pack.name }));

	const set = useCallback((key: keyof PlanningFormState, value: string) => {
		setForm((current) => ({ ...current, [key]: value }));
		setErrors((current) => ({ ...current, [key]: '' }));
		if (key === 'method' || key === 'startMonth' || key === 'endMonth') {
			setSectors([]);
		}
	}, []);

	const fertilizerDemand = useMemo(
		() => mapPlanningFertilizerDemand(sectors, demandView === 'hectare', fertilizers),
		[demandView, fertilizers, sectors]
	);

	const demandPeriods = useMemo(
		() => fertilizerDemand.rows.filter((row) => !row.isTotal).map((row) => row.id),
		[fertilizerDemand]
	);
	const adjustment = useDemandAdjustment({
		advice,
		sectors,
		demandPeriods,
		products: fertilizers,
	});

	const handleCalculate = async () => {
		if (!adviceId) return;
		const parsed = planningSchema.safeParse(form);
		if (!parsed.success) {
			const fieldErrors: Record<string, string> = {};
			parsed.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			return;
		}

		try {
			const productPrices = parsed.data.method === 'irrigation' ? costs.buildProductPrices() : [];
			const payload = {
				planId: adviceId,
				method: parsed.data.method,
				period: [toApiPeriod(parsed.data.startMonth), toApiPeriod(parsed.data.endMonth)] as [
					string,
					string,
				],
				...(parsed.data.method === 'irrigation' ? { packId: parsed.data.packId } : {}),
				...(productPrices.length ? { productPrices } : {}),
			};
			const result = (await calculatePlan(payload)) as PlanningSectorResult[];
			setSectors(result);
			setPersisted({
				form,
				sectors: result,
				prices: costs.prices,
				pricesUpdatedAt: costs.pricesUpdatedAt,
			});
			setErrors({});
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao calcular o planejamento.');
		}
	};

	const handleClear = () => {
		setForm(EMPTY_FORM);
		setSectors([]);
		costs.restoreCosts(null);
		setErrors({});
		setDemandView('hectare');
	};

	const handleSave = () => {
		if (!persisted) return;
		setForm(persisted.form);
		setSectors(persisted.sectors);
		costs.restoreCosts(persisted);
		setErrors({});
		setIsView(true);
		setDemandView('hectare');
		onSaved?.(persisted.form.method);
	};

	const handleEdit = () => setIsView(false);

	const openPackModal = (mode: PlanningPackModalMode, pack?: FertilizerPack | null) => {
		setPackModal({ open: true, mode, pack: pack ?? null });
	};

	const closePackModal = () => setPackModal((current) => ({ ...current, open: false }));

	const handlePackSaved = (pack: FertilizerPack) => {
		setForm((current) => ({ ...current, packId: pack._id }));
		setErrors((current) => ({ ...current, packId: '' }));
	};

	const handleDuplicatePack = async () => {
		if (!form.packId) return;
		try {
			await duplicatePack(form.packId);
			toast.success('Pacote duplicado com sucesso.');
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao duplicar o pacote.');
		}
	};

	const handleClearPack = () => {
		setForm((current) => ({ ...current, packId: '' }));
		setErrors((current) => ({ ...current, packId: '' }));
	};

	const buildExportData = (): PlanningExportData => ({
		fileName: `planejamento-${advice.name || adviceId}`,
		info: [
			['Recomendação', advice.name || '—'],
			['Método de aplicação', methodLabel],
			['Mês inicial', startMonthLabel],
			['Mês final', endMonthLabel],
			['Pacote de produtos', packLabel],
		],
		sections: [
			{
				title: 'Demanda por hectare',
				table: mapPlanningFertilizerDemand(sectors, true, fertilizers),
			},
			{
				title: 'Demanda por setor',
				table: mapPlanningFertilizerDemand(sectors, false, fertilizers),
			},
			...(adjustment.adjustedSectors.length
				? [
						{
							title: 'Demanda ajustada por hectare',
							table: mapPlanningAdjustedDemand(
								sectors,
								adjustment.adjustedSectors,
								true,
								fertilizers
							),
						},
						{
							title: 'Demanda ajustada por setor',
							table: mapPlanningAdjustedDemand(
								sectors,
								adjustment.adjustedSectors,
								false,
								fertilizers
							),
						},
					]
				: []),
			...(budget.hasBudget
				? buildBudgetExportSections(budget.budgetMonths, (period) =>
						mapPlanningBudgetTable(sectors, period, fertilizers)
					)
				: []),
		],
	});

	const handleExportExcel = () => exportPlanningToExcel(buildExportData());

	const handleExportPdf = () => exportPlanningToPdf(buildExportData());

	const methodLabel = form.method ? APPLICATION_METHOD_LABELS[form.method] : '—';
	const startMonthLabel = form.startMonth
		? (MONTH_NAMES_PT[form.startMonth.split('-')[1]] ?? form.startMonth)
		: '—';
	const endMonthLabel = form.endMonth
		? (MONTH_NAMES_PT[form.endMonth.split('-')[1]] ?? form.endMonth)
		: '—';
	const packLabel = selectedPack?.name || '—';

	return {
		form,
		errors,
		set,
		planMonths,
		isView,
		hasCalculated,
		isCalculating,
		showIrrigationFields,
		demandView,
		setDemandView,
		fertilizerDemand,
		packOptions,
		selectedPack,
		packModal,
		openPackModal,
		closePackModal,
		handlePackSaved,
		handleDuplicatePack,
		handleClearPack,
		isDuplicatingPack,
		packProducts: costs.packProducts,
		prices: costs.prices,
		pricesUpdatedAt: costs.pricesUpdatedAt,
		informedCostsCount: costs.informedCostsCount,
		isCostsComplete: costs.isCostsComplete,
		costsModal: costs.costsModal,
		openCostsModal: costs.openCostsModal,
		closeCostsModal: costs.closeCostsModal,
		editCosts: costs.editCosts,
		handleCostsSaved: costs.handleCostsSaved,
		...budget,
		handleExportExcel,
		handleExportPdf,
		handleCalculate,
		handleClear,
		handleSave,
		handleEdit,
		adjustment,
		methodLabel,
		startMonthLabel,
		endMonthLabel,
		packLabel,
	};
};
